import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, CircleCheck, Circle, RefreshCw, Reply, Sparkles } from 'lucide-react';
import type { MessageDetail, SummaryResponse } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { qk, usePrefs } from '@/lib/queries';
import { composeReply } from '@/lib/compose';
import { cn, IconButton, tint } from './ui';

const SENTIMENT: Record<string, { label: string; color: string }> = {
  positive: { label: 'Positive', color: 'var(--sent-positive)' },
  neutral: { label: 'Neutral', color: 'var(--sent-neutral)' },
  negative: { label: 'Tense', color: 'var(--sent-negative)' },
  urgent: { label: 'Urgent', color: 'var(--sent-urgent)' },
};

/** "Conversation intelligence": AI summary, key points, action items and one-click smart replies. */
export function InsightsCard({ messageId, threadSize, latest }: { messageId: string; threadSize: number; latest?: MessageDetail }) {
  const prefs = usePrefs().data;
  const qc = useQueryClient();
  const auto = prefs?.ai?.autoSummarize !== false && (threadSize >= 2 || (latest?.textBody?.length ?? latest?.htmlBody?.length ?? 0) > 1400);
  const [requested, setRequested] = useState(false);
  const [open, setOpen] = useState(true);
  const [done, setDone] = useState<Set<number>>(new Set());
  const q = useQuery({
    queryKey: qk.summary(messageId),
    queryFn: () => api.summarize(messageId),
    enabled: auto || requested,
    staleTime: Infinity,
    retry: 0,
  });

  const refresh = async () => {
    const fresh = await api.summarize(messageId, true);
    qc.setQueryData<SummaryResponse>(qk.summary(messageId), fresh);
  };

  if (!auto && !requested) {
    return (
      <button
        onClick={() => setRequested(true)}
        className="ai-ring group flex w-full items-center gap-3 rounded-2xl bg-raised/60 px-4 py-3 text-left transition hover:bg-raised"
      >
        <span className="flex size-8 items-center justify-center rounded-xl ai-gradient text-white"><Sparkles className="size-4" /></span>
        <span className="flex-1">
          <span className="block text-[13.5px] font-semibold">Summarize & suggest replies</span>
          <span className="block text-[12px] text-muted">TL;DR, action items and smart replies from AI</span>
        </span>
      </button>
    );
  }

  const s = q.data?.summary;
  const sentiment = s ? SENTIMENT[s.sentiment] ?? SENTIMENT.neutral : null;

  return (
    <motion.div layout className={cn('ai-ring overflow-hidden rounded-2xl bg-raised/70 backdrop-blur', q.isFetching && 'busy')}>
      <div className="flex items-center gap-2.5 px-4 pt-3.5">
        <span className="flex size-6 items-center justify-center rounded-lg ai-gradient text-white"><Sparkles className="size-3.5" /></span>
        <span className="text-[12px] font-semibold uppercase tracking-[0.08em] ai-text">Conversation intelligence</span>
        {sentiment && (
          <span className="pill badge rounded-full px-2 py-px text-[11px] font-semibold" style={{ background: tint(sentiment.color), color: sentiment.color }}>
            {sentiment.label}
          </span>
        )}
        {s?.needsReply && <span className="rounded-full bg-danger/15 px-2 py-px text-[11px] font-semibold text-danger">Awaiting your reply</span>}
        <span className="flex-1" />
        {q.data && (
          <IconButton size="sm" label="Regenerate" onClick={() => void refresh()}>
            <RefreshCw />
          </IconButton>
        )}
        <IconButton size="sm" label={open ? 'Collapse' : 'Expand'} onClick={() => setOpen(!open)}>
          <ChevronDown className={cn('transition', !open && '-rotate-90')} />
        </IconButton>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="px-4 pb-4">
            {q.isLoading || (q.isFetching && !q.data) ? (
              <div className="space-y-2 pt-3">
                <div className="shimmer-text text-[14px] font-medium">Reading the conversation…</div>
                <div className="skeleton h-3 w-11/12" />
                <div className="skeleton h-3 w-4/5" />
                <div className="skeleton h-3 w-2/3" />
              </div>
            ) : q.isError ? (
              <div className="pt-3 text-[13px] text-danger">Couldn't summarize: {errorMessage(q.error)}</div>
            ) : s ? (
              <div className="selectable pt-2.5">
                <p className="text-[14.5px] leading-relaxed text-fg">{s.tldr}</p>
                {s.keyPoints.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {s.keyPoints.map((k, i) => (
                      <li key={i} className="flex gap-2 text-[13px] text-fg/85">
                        <span className="mt-[7px] size-1.5 shrink-0 rounded-full ai-gradient" />
                        {k}
                      </li>
                    ))}
                  </ul>
                )}
                {s.actionItems.length > 0 && (
                  <div className="mt-4">
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-faint">Action items</div>
                    <div className="space-y-1">
                      {s.actionItems.map((a, i) => (
                        <button
                          key={i}
                          onClick={() => setDone((d) => new Set(d.has(i) ? [...d].filter((x) => x !== i) : [...d, i]))}
                          className="flex w-full items-start gap-2.5 rounded-lg px-1.5 py-1 text-left text-[13px] transition hover:bg-line"
                        >
                          {done.has(i) ? <CircleCheck className="mt-0.5 size-4 shrink-0 text-ok" /> : <Circle className="mt-0.5 size-4 shrink-0 text-faint" />}
                          <span className={cn('flex-1', done.has(i) && 'text-faint line-through')}>{a.task}</span>
                          <span className={cn('shrink-0 rounded-md px-1.5 py-px text-[11px] font-medium', a.owner === 'me' ? 'bg-accent/15 text-accent' : 'bg-line text-muted')}>
                            {a.owner === 'me' ? 'You' : a.owner}
                          </span>
                          {a.due && <span className="shrink-0 text-[11.5px] text-muted">{a.due}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {latest && s.suggestedReplies.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {s.suggestedReplies.map((r, i) => (
                      <motion.button
                        key={r.label}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 * i }}
                        onClick={() => composeReply(latest, false, r.instruction)}
                        title={r.instruction}
                        className="group inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-raised px-3 py-1.5 text-[12.5px] font-medium transition hover:border-accent/50 hover:bg-accent/10 hover:text-accent active:scale-95"
                      >
                        <Reply className="size-3.5 opacity-60 group-hover:opacity-100" />
                        {r.label}
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
